import React, { useMemo, useState } from 'react';
import { Button, TextField, Box, Alert, CircularProgress, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useForgotPasswordMutation } from '../../api/use-forgot-password-mutation';
import { getSessionRoleForRestore } from '../../services/auth';
import { USER_ROLES } from '../../constants';
import AuthPageShell from './AuthPageShell';
import { authPrimaryButtonSx, textFieldStyle } from './AuthStyles';

const ForgotPassword = () => {
  const sessionRole = useMemo(() => getSessionRoleForRestore(), []);
  const loginPath = sessionRole === USER_ROLES.PARTNER ? '/partner-login' : '/login';
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const forgotPasswordMutation = useForgotPasswordMutation({
    onSuccess: (result) => {
      setSuccess(result.message);
      setEmail('');
      setError('');
    },
    onError: (err) => {
      setError(err?.message || 'Failed to send password reset email');
      setSuccess('');
    },
  });

  const isLoading = forgotPasswordMutation.isPending;

  const handleSubmit = (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    forgotPasswordMutation.mutate({ email, role: sessionRole });
  };

  return (
    <AuthPageShell
      title="Forgot Password"
      subtitle="Enter your email address and we'll send you a link to reset your password."
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          margin="normal"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
          sx={textFieldStyle}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disableElevation
          disabled={isLoading}
          sx={authPrimaryButtonSx}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
        </Button>
        <Box textAlign="center">
          <Link component={RouterLink} to={loginPath} underline="hover" color="primary.main">
            Back to Login
          </Link>
        </Box>
      </form>
    </AuthPageShell>
  );
};

export default ForgotPassword;
